import styled from "@emotion/styled/macro";
import { css } from "@emotion/react";

/* ─── Theme: warm teal-green ─── */
const accent = "#00796b";
const accentDark = "#004d40";
const accentLight = "#e0f2f1";

/* ─── Page Wrapper ─── */
export const BPPageWrapper = styled.div`
    padding: 0 20px; width: 100%;
    @media (max-width: 768px) { padding: 0 12px; }
`;

/* ─── Header ─── */
export const BPHeader = styled.div`
    display: flex; align-items: center; justify-content: space-between;
    padding: 16px 20px;
    background: linear-gradient(135deg, ${accent} 0%, ${accentDark} 100%);
    color: #fff; border-radius: 10px; margin-bottom: 20px;
    flex-wrap: wrap; gap: 12px;
    box-shadow: 0 4px 12px rgba(0,121,107,0.25);
    @media (max-width: 768px) { flex-direction: column; align-items: flex-start; padding: 14px 16px; }
`;

export const BPHeaderTitle = styled.div`
    h3 { margin: 0 0 4px 0; font-size: 1.15rem; font-weight: 600; }
    p { margin: 0; font-size: 0.82rem; opacity: 0.85; }
`;

export const BPHeaderStats = styled.div`
    display: flex; gap: 12px; flex-wrap: wrap;
    @media (max-width: 768px) { gap: 8px; width: 100%; }
`;

export const StatBadge = styled.div`
    display: flex; align-items: center; gap: 5px;
    padding: 5px 12px; background: rgba(255,255,255,0.15);
    border-radius: 18px; font-size: 0.78rem; font-weight: 500;
    span.count { font-weight: 700; font-size: 0.95rem; }
`;

/* ─── Tab Bar ─── */
export const TabBar = styled.div`
    display: flex; gap: 4px; background: #f0f2f5; border-radius: 10px;
    padding: 4px; margin-bottom: 20px;
    @media (max-width: 480px) { flex-wrap: wrap; }
`;

export const TabBtn = styled.button`
    flex: 1; padding: 10px 16px; border: none; border-radius: 8px;
    font-size: 0.84rem; font-weight: 600; cursor: pointer; transition: all 0.2s;
    display: flex; align-items: center; justify-content: center; gap: 6px;
    min-width: 120px;
    background: ${function(p) { return p.active ? accent : "transparent"; }};
    color: ${function(p) { return p.active ? "#fff" : "#546e7a"; }};
    &:hover { background: ${function(p) { return p.active ? accent : "#e0e0e0"; }}; }
    .tab-badge {
        display: inline-flex; align-items: center; justify-content: center;
        width: 20px; height: 20px; border-radius: 50%; font-size: 0.65rem;
        font-weight: 700;
        background: ${function(p) { return p.active ? "rgba(255,255,255,0.25)" : "#cfd8dc"; }};
        color: ${function(p) { return p.active ? "#fff" : "#546e7a"; }};
    }
`;

/* ─── Overall Progress Ring ─── */
export const ProgressRingSection = styled.div`
    display: flex; align-items: center; gap: 20px; padding: 16px 20px;
    background: #fff; border-radius: 10px; margin-bottom: 20px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.06); border: 1px solid #e8edf2;
    flex-wrap: wrap;
    @media (max-width: 480px) { flex-direction: column; text-align: center; }
`;

export const RingContainer = styled.div`
    position: relative; width: 80px; height: 80px; flex-shrink: 0;
`;

export const RingSvg = styled.svg`
    transform: rotate(-90deg); width: 80px; height: 80px;
`;

export const RingLabel = styled.div`
    position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
    font-size: 1.1rem; font-weight: 700; color: ${accent};
`;

export const ProgressDetails = styled.div`
    flex: 1;
    h4 { margin: 0 0 8px 0; font-size: 0.95rem; font-weight: 600; color: #263238; }
`;

export const ProgressRow = styled.div`
    display: flex; gap: 20px; flex-wrap: wrap;
`;

export const ProgressItem = styled.div`
    display: flex; align-items: center; gap: 8px; font-size: 0.78rem;
    .pi-bar { width: 60px; height: 5px; background: #e0e0e0; border-radius: 3px; overflow: hidden; }
    .pi-fill { height: 100%; border-radius: 3px; transition: width 0.4s;
        background: ${function(p) { return p.percent >= 100 ? "#43a047" : accent; }};
        width: ${function(p) { return p.percent || 0; }}%;
    }
    .pi-text { color: #78909c; white-space: nowrap; }
`;

/* ─── Alert Banner ─── */
export const DangerAlertBanner = styled.div`
    display: flex; align-items: center; gap: 12px;
    padding: 12px 18px; border-radius: 8px; margin-bottom: 20px;
    flex-wrap: wrap;
    background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%);
    border-left: 4px solid #e53935;
    box-shadow: 0 1px 4px rgba(0,0,0,0.06);
    .alert-icon { font-size: 1.8rem; }
    .alert-text { flex: 1; }
    .alert-title { font-size: 0.88rem; font-weight: 700; color: #b71c1c; }
    .alert-desc { font-size: 0.78rem; color: #c62828; }
`;

export const AlertActionBtn = styled.button`
    padding: 6px 14px; border-radius: 6px; border: 1.5px solid #e53935;
    background: #fff; color: #e53935; font-size: 0.78rem; font-weight: 600;
    cursor: pointer; transition: all 0.2s; white-space: nowrap;
    &:hover { background: #e53935; color: #fff; }
`;

/* ═══════════ Checklist Card ═══════════ */
export const ChecklistSection = styled.div`
    background: #fff; border-radius: 10px; border: 1px solid #e8edf2;
    margin-bottom: 20px; overflow: hidden;
    box-shadow: 0 1px 4px rgba(0,0,0,0.04);
`;

export const ChecklistHeader = styled.div`
    padding: 14px 20px; background: ${accentLight}; border-bottom: 1px solid #b2dfdb;
    display: flex; align-items: center; justify-content: space-between;
    h4 { margin: 0; font-size: 0.92rem; font-weight: 600; color: ${accentDark}; }
    .ch-badge { font-size: 0.72rem; font-weight: 600; color: ${accent};
        background: rgba(0,121,107,0.1); padding: 3px 10px; border-radius: 10px; }
`;

export const ChecklistBody = styled.div` padding: 16px 20px; `;

export const CheckItem = styled.div`
    display: flex; align-items: flex-start; gap: 12px; padding: 12px 0;
    border-bottom: 1px solid #f0f2f5;
    &:last-child { border-bottom: none; }
    ${function(p) { return p.checked ? css`opacity: 1;` : css`opacity: 0.85;`; }}
`;

export const CheckToggle = styled.button`
    width: 28px; height: 28px; border-radius: 6px; border: 2px solid;
    cursor: pointer; display: flex; align-items: center; justify-content: center;
    flex-shrink: 0; transition: all 0.2s; font-size: 0.85rem;
    ${function(p) {
        if (p.checked) return css`
            background: ${accent}; border-color: ${accent}; color: #fff;
        `;
        return css`
            background: #fff; border-color: #cfd8dc; color: transparent;
            &:hover { border-color: ${accent}; }
        `;
    }}
`;

export const CheckContent = styled.div`
    flex: 1;
    .ck-label { font-size: 0.88rem; font-weight: 600; color: #263238;
        display: flex; align-items: center; gap: 6px; margin-bottom: 4px; }
    .ck-icon { font-size: 1.1rem; }
`;

export const DetailInput = styled.div`
    margin-top: 8px;
    input, select {
        padding: 8px 12px; border: 1px solid #cfd8dc; border-radius: 6px;
        font-size: 0.84rem; color: #263238; outline: none; width: 100%;
        max-width: 320px; transition: border 0.2s;
        &:focus { border-color: ${accent}; box-shadow: 0 0 0 2px rgba(0,121,107,0.12); }
    }
    label { display: block; font-size: 0.72rem; font-weight: 600; color: #78909c;
        text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 4px; }
`;

export const DetailRow = styled.div`
    display: flex; gap: 12px; flex-wrap: wrap;
    @media (max-width: 480px) { flex-direction: column; }
`;

/* ─── Items to Prepare Grid ─── */
export const ItemsGrid = styled.div`
    display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px;
    @media (max-width: 768px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

export const ItemChip = styled.button`
    display: flex; align-items: center; gap: 6px; padding: 8px 12px;
    border-radius: 8px; font-size: 0.8rem; cursor: pointer;
    transition: all 0.2s; text-align: left; border: 1.5px solid;
    ${function(p) {
        if (p.checked) return css`
            background: #e8f5e9; border-color: #43a047; color: #2e7d32;
        `;
        return css`
            background: #fafafa; border-color: #e0e0e0; color: #616161;
            &:hover { border-color: #90a4ae; background: #f5f5f5; }
        `;
    }}
    .chip-check { font-size: 0.8rem; flex-shrink: 0; }
`;

/* ─── Counselling Checklist ─── */
export const CounselGrid = styled.div`
    display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px;
    @media (max-width: 768px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

export const CounselCard = styled.button`
    display: flex; align-items: center; gap: 10px; padding: 12px 14px;
    border-radius: 8px; cursor: pointer; transition: all 0.2s;
    text-align: left; border: 1.5px solid;
    ${function(p) {
        if (p.checked) return css`
            background: ${accentLight}; border-color: ${accent};
        `;
        return css`
            background: #fafafa; border-color: #e0e0e0;
            &:hover { border-color: #90a4ae; }
        `;
    }}
    .cc-icon { font-size: 1.2rem; flex-shrink: 0; }
    .cc-text { flex: 1; }
    .cc-label { font-size: 0.82rem; font-weight: 600;
        color: ${function(p) { return p.checked ? accentDark : "#455a64"; }}; }
    .cc-desc { font-size: 0.7rem; color: #90a4ae; margin-top: 2px; }
    .cc-check { font-size: 0.85rem; flex-shrink: 0;
        color: ${function(p) { return p.checked ? accent : "#cfd8dc"; }}; }
`;

/* ═══════════ Danger Signs ═══════════ */
export const DangerSection = styled.div`
    background: #fff; border-radius: 10px; border: 1px solid #e8edf2;
    margin-bottom: 20px; overflow: hidden;
    box-shadow: 0 1px 4px rgba(0,0,0,0.04);
`;

export const DangerHeader = styled.div`
    padding: 14px 20px; background: #fce4ec; border-bottom: 1px solid #f8bbd0;
    display: flex; align-items: center; justify-content: space-between;
    h4 { margin: 0; font-size: 0.92rem; font-weight: 600; color: #b71c1c; }
`;

export const DangerBody = styled.div` padding: 16px 20px; `;

export const DangerTimeline = styled.div`
    display: flex; flex-direction: column; gap: 12px;
`;

export const DangerEntry = styled.div`
    display: flex; gap: 12px; padding: 12px 16px;
    border-radius: 8px; border: 1px solid;
    ${function(p) {
        if (p.severity === "critical") return css`
            background: #ffebee; border-color: #ef9a9a;
        `;
        if (p.severity === "high") return css`
            background: #fff3e0; border-color: #ffcc80;
        `;
        return css`
            background: #fff8e1; border-color: #ffe082;
        `;
    }}
    .de-icon { font-size: 1.3rem; flex-shrink: 0; margin-top: 2px; }
    .de-content { flex: 1; }
    .de-title { font-size: 0.88rem; font-weight: 600; color: #263238; }
    .de-meta { font-size: 0.75rem; color: #78909c; margin-top: 2px; }
    .de-action { font-size: 0.8rem; color: #455a64; margin-top: 4px; }
    .de-outcome { display: inline-block; padding: 2px 8px; border-radius: 4px;
        font-size: 0.7rem; font-weight: 600; margin-top: 4px;
        ${function(p) {
            if (p.resolved) return css`background: #e8f5e9; color: #2e7d32;`;
            return css`background: #fff3e0; color: #e65100;`;
        }}
    }
`;

export const ReportDangerBtn = styled.button`
    padding: 10px 20px; border-radius: 8px; border: none;
    background: #e53935; color: #fff; font-size: 0.85rem; font-weight: 600;
    cursor: pointer; transition: all 0.2s; display: flex; align-items: center; gap: 6px;
    &:hover { background: #c62828; }
`;

/* ─── Save Button ─── */
export const SaveBtn = styled.button`
    padding: 12px 32px; border-radius: 8px; border: none;
    background: ${accent}; color: #fff; font-size: 0.9rem; font-weight: 600;
    cursor: pointer; transition: all 0.2s;
    &:hover { background: ${accentDark}; }
    &:disabled { background: #80cbc4; cursor: not-allowed; }
`;

/* ─── Dialog ─── */
export const DialogOverlay = styled.div`
    position: fixed; top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0,0,0,0.45); z-index: 1200;
    display: flex; align-items: center; justify-content: center; padding: 20px;
`;

export const DialogBox = styled.div`
    background: #fff; border-radius: 12px; width: 100%; max-width: 500px;
    box-shadow: 0 20px 60px rgba(0,0,0,0.15); overflow: hidden;
`;

export const DialogHeader = styled.div`
    padding: 18px 24px; background: #fce4ec; border-bottom: 1px solid #f8bbd0;
    display: flex; align-items: center; justify-content: space-between;
    h4 { margin: 0; font-size: 1rem; font-weight: 600; color: #b71c1c; }
`;

export const DialogBody = styled.div` padding: 24px; `;

export const DialogFooter = styled.div`
    padding: 16px 24px; border-top: 1px solid #e8edf2;
    display: flex; justify-content: flex-end; gap: 10px;
`;

export const DialogCloseBtn = styled.button`
    background: none; border: none; font-size: 1.2rem;
    cursor: pointer; color: #90a4ae; padding: 0; line-height: 1;
    &:hover { color: #546e7a; }
`;

export const DialogBtn = styled.button`
    padding: 8px 20px; border-radius: 6px; font-size: 0.85rem;
    font-weight: 600; cursor: pointer; border: none; transition: all 0.2s;
    ${function(p) {
        if (p.variant === "danger") return css`
            background: #e53935; color: #fff;
            &:hover { background: #c62828; }
            &:disabled { background: #ef9a9a; cursor: not-allowed; }
        `;
        return css`
            background: #f0f2f5; color: #546e7a;
            &:hover { background: #e0e0e0; }
        `;
    }}
`;

export const FormField = styled.div`
    margin-bottom: 16px;
    label { display: block; font-size: 0.78rem; font-weight: 600; color: #546e7a;
        text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px; }
    input, textarea, select {
        width: 100%; padding: 10px 12px; border: 1px solid #cfd8dc;
        border-radius: 6px; font-size: 0.88rem; color: #263238; outline: none;
        transition: border 0.2s;
        &:focus { border-color: #e53935; box-shadow: 0 0 0 2px rgba(229,57,53,0.12); }
    }
    textarea { resize: vertical; min-height: 60px; }
`;

/* ─── Empty / Loading ─── */
export const EmptyState = styled.div`
    display: flex; flex-direction: column; align-items: center;
    justify-content: center; padding: 60px 20px; text-align: center; color: #90a4ae;
    .empty-icon { font-size: 3rem; margin-bottom: 16px; }
    .empty-title { font-size: 1.05rem; font-weight: 600; color: #546e7a; margin-bottom: 8px; }
    .empty-desc { font-size: 0.85rem; max-width: 380px; line-height: 1.5; }
`;

export const LoadingWrapper = styled.div`
    display: flex; align-items: center; justify-content: center;
    padding: 60px 20px; color: #90a4ae; font-size: 0.9rem; gap: 10px;
`;

export const GoToBookingBtn = styled.button`
    margin-top: 20px; padding: 10px 24px; background: ${accent}; color: #fff;
    border: none; border-radius: 8px; font-size: 0.9rem; font-weight: 600;
    cursor: pointer; transition: background 0.2s;
    &:hover { background: ${accentDark}; }
`;
